import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import {
  buildPatientMedicalTimeline,
  medicalRecordTypeLabel,
  type MedicalRecordEvent,
} from "@/core/patients/medical-records";
import {
  listPatients,
  patientDetailPath,
  searchPatients,
} from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { formatInvoiceDate } from "@/core/pharmacy/format";
import { routes } from "@/router/all_routes";

const TYPE_BADGE: Record<MedicalRecordEvent["type"], string> = {
  admission: "bg-info-subtle text-info-emphasis",
  appointment: "bg-primary-subtle text-primary-emphasis",
  consultation: "bg-success-subtle text-success-emphasis",
  prescription: "bg-warning-subtle text-warning-emphasis",
  lab: "bg-secondary-subtle text-secondary-emphasis",
  imaging: "bg-secondary-subtle text-secondary-emphasis",
  vital_sign: "bg-danger-subtle text-danger-emphasis",
  hospitalization: "bg-info-subtle text-info-emphasis",
  invoice: "bg-dark-subtle text-dark-emphasis",
};

export function PatientRecordsPage() {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(() => searchParams.get("patientId") ?? "");

  const patients = useMemo(
    () => (query.trim() ? searchPatients(query) : listPatients()),
    [query],
  );

  const selectedPatient = useMemo(
    () => patients.find((p) => p.id === selectedId) ?? patients[0] ?? null,
    [patients, selectedId],
  );

  const timeline = useMemo(() => {
    if (!selectedPatient) return [];
    return buildPatientMedicalTimeline(selectedPatient);
  }, [selectedPatient]);

  return (
    <div>
      <PageHeader
        title="Dossiers médicaux"
        description="Historique clinique unifié — admissions, rendez-vous, consultations, prescriptions, labo et pharmacie."
        actions={
          <div className="d-flex gap-2 flex-wrap ph-page-header-actions">
            <Link to={routes.patients} className="btn btn-outline-secondary btn-sm">
              Liste patients
            </Link>
            <Link to={routes.appointments} className="btn btn-outline-primary btn-sm">
              Rendez-vous
            </Link>
          </div>
        }
      />

      <div className="row g-3 ph-split-workspace">
        <div className="col-lg-4">
          <div className="ph-invoice-sheet">
            <label className="form-label small fw-semibold" htmlFor="rec-search">
              Rechercher un patient
            </label>
            <input
              id="rec-search"
              type="search"
              className="form-control form-control-sm mb-3"
              placeholder="Nom, n° dossier…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className="list-group list-group-flush ph-records-patient-list">
              {patients.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`list-group-item list-group-item-action py-2 ${selectedPatient?.id === p.id ? "active" : ""}`}
                  onClick={() => setSelectedId(p.id)}
                >
                  <div className="fw-medium">{patientFullName(p)}</div>
                  <div className="small opacity-75">{p.fileNumber}</div>
                </button>
              ))}
            </div>
            {patients.length === 0 && (
              <p className="small text-muted mb-0">Aucun patient trouvé.</p>
            )}
          </div>
        </div>

        <div className="col-lg-8">
          {!selectedPatient ? (
            <div className="ph-invoice-sheet">
              <p className="text-muted mb-0">Sélectionnez un patient pour afficher son dossier.</p>
            </div>
          ) : (
            <>
              <div className="ph-invoice-sheet mb-3">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 ph-split-detail-head">
                  <div>
                    <h2 className="h5 mb-1">{patientFullName(selectedPatient)}</h2>
                    <p className="small text-muted mb-0">
                      Dossier {selectedPatient.fileNumber} · {timeline.length} événement
                      {timeline.length !== 1 ? "s" : ""}
                    </p>
                  </div>
                  <Link
                    to={patientDetailPath(selectedPatient.id)}
                    className="btn btn-outline-primary btn-sm"
                  >
                    Fiche patient
                  </Link>
                </div>
              </div>

              <div className="ph-invoice-sheet">
                <h3 className="h6 mb-3">Chronologie médicale</h3>
                {timeline.length === 0 ? (
                  <p className="text-muted mb-0">
                    Aucun événement clinique enregistré pour ce patient.
                  </p>
                ) : (
                  <ul className="list-unstyled mb-0 hosp-med-timeline">
                    {timeline.map((event) => (
                      <li key={`${event.type}-${event.id}`} className="hosp-med-timeline-item">
                        <div className="d-flex flex-wrap gap-2 align-items-center mb-1">
                          <span className={`badge ${TYPE_BADGE[event.type]}`}>
                            {medicalRecordTypeLabel(event.type)}
                          </span>
                          <span className="small text-muted">
                            {formatInvoiceDate(event.date)}
                          </span>
                          {event.statusLabel && (
                            <span className="small text-muted">· {event.statusLabel}</span>
                          )}
                        </div>
                        <div className="fw-medium">{event.title}</div>
                        <p className="small text-muted mb-1">{event.summary}</p>
                        {event.linkPath && (
                          <Link to={event.linkPath} className="small">
                            Voir le détail →
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
